"use client"

import { css, Global } from "@emotion/react"
import styled from "@emotion/styled"
import { Link, Locale } from "@eurodrive/utils"
import { Box, IconButton } from "@mui/material"
import Image from "next/image"
import { FC, useEffect, useRef, useState } from "react"
import Close from "../icons/Close"
import { sharedColors as colors } from "../theme/colors"

const STORAGE_KEY = "cookieBannerDismissedAt"
const SIX_MONTHS_MS = 183 * 24 * 60 * 60 * 1000

const StyledBanner = styled(Box)`
  position: fixed;
  bottom: 0;
  inset-inline: 0;
  z-index: 1200;
  background: ${colors.white};
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 20px;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: center;
    justify-content: space-between;
    gap: 40px;
    padding: 30px 40px;
  }
`

const StyledTextContainer = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
    flex: initial;
  }
`

const StyledText = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.4;
  color: ${colors.black};
  text-align: start;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

const StyledPrivacyLink = styled(Link)`
  color: ${colors.black};
  text-decoration: underline;
`

const StyledCloseButton = styled(IconButton)`
  padding: 0;
  flex-shrink: 0;
`

const texts = {
  he: {
    beforeLink:
      "האתר עושה שימוש בעוגיות כדי לשפר את חוויית הגלישה שלך, להציג תכנים מותאמים אישית ועוד. למידע נוסף אנא קראו את ",
    link: "מדיניות הפרטיות",
    afterLink: " שלנו.",
    close: "סגירת הודעת העוגיות",
    region: "הודעה על שימוש בעוגיות",
  },
  en: {
    beforeLink:
      "This site uses cookies to improve your browsing experience, display personalized content and more. For more information, please read our ",
    link: "Privacy Policy",
    afterLink: ".",
    close: "Dismiss cookie notice",
    region: "Cookie usage notice",
  },
}

interface CookieBannerProps {
  locale: string
}

const CookieBanner: FC<CookieBannerProps> = ({ locale }) => {
  const [visible, setVisible] = useState(false)
  const [bannerHeight, setBannerHeight] = useState(0)
  const bannerRef = useRef<HTMLDivElement>(null)
  const t = texts[locale as Locale] ?? texts.he

  // The WhatsApp widget is fixed to the bottom-right corner with a huge
  // z-index, so it would cover the banner — lift it above while visible
  useEffect(() => {
    const el = bannerRef.current
    if (!visible || !el) {
      setBannerHeight(0)
      return
    }
    const update = () => setBannerHeight(el.offsetHeight)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [visible])

  useEffect(() => {
    try {
      const dismissedAt = Number(window.localStorage.getItem(STORAGE_KEY))
      if (!dismissedAt || Date.now() - dismissedAt > SIX_MONTHS_MS) {
        setVisible(true)
      }
    } catch {
      // localStorage unavailable (private mode, blocked) — show the banner
      setVisible(true)
    }
  }, [])

  const handleDismiss = () => {
    setVisible(false)
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()))
    } catch {
      // Persistence unavailable — banner stays dismissed for this page view only
    }
  }

  if (!visible) {
    return null
  }

  return (
    <StyledBanner ref={bannerRef} role="region" aria-label={t.region}>
      {bannerHeight > 0 && (
        <Global
          styles={css`
            /* Outranks the widget's own "#wa-widget-send-button { bottom: 0
               !important }" rule, which loads after ours */
            #whatsapp_chat_widget #wa-widget-send-button {
              bottom: ${bannerHeight}px !important;
            }
          `}
        />
      )}
      <StyledTextContainer>
        <Image
          src="/icons/cookie-icon.svg"
          alt=""
          width={40}
          height={40}
          aria-hidden
        />
        <StyledText>
          {t.beforeLink}
          <StyledPrivacyLink href="/privacy-policy">{t.link}</StyledPrivacyLink>
          {t.afterLink}
        </StyledText>
      </StyledTextContainer>
      <StyledCloseButton aria-label={t.close} onClick={handleDismiss}>
        <Close width={24} height={24} />
      </StyledCloseButton>
    </StyledBanner>
  )
}

export default CookieBanner
